import * as React from "react"
import {
  Button,
  Heading,
  StyledPanelBodySection,
  StyledPanelBoilerplate,
  ThemeCss,
} from "gatsby-interface"
import { gatsbyCloudCdnIntegrationConfig as text } from "@modules/locales/default.js"
import { useOrganizationPlanFieldsFragment } from "@modules/billing/shared/fragments.generated"
import {
  HostingDetails,
  HostingCertificate,
  CertificateStatus,
  MachinePricingTier,
} from "@modules/graphql/types"
import { CustomCertificateForm } from "./CustomCertificateForm"
import {
  SslCertificateDetails,
  SslCertificateIsGenerating,
} from "../../shared/components/SslCertificateDetails"
import { RemoveCertificateForm } from "./RemoveCertificateForm"
import { useTracker, SegmentEventType } from "@modules/analytics"
import { useFlags } from "@modules/featureFlags"

const rootCss: ThemeCss = theme => ({
  borderTop: `1px solid ${theme.colors.standardLine}`,
  marginTop: theme.space[5],
})

const headerCss: ThemeCss = theme => ({
  alignItems: `center`,
  borderBottom: `1px solid ${theme.colors.grey[20]}`,
  display: `flex`,
  justifyContent: `space-between`,
  marginTop: theme.space[5],
  border: `none`,
  paddingBottom: 0,
})

const headingCss: ThemeCss = theme => ({
  fontFamily: theme.fonts.body,
  fontSize: theme.fontSizes[2],
  fontWeight: theme.fontWeights.semiBold,
})

const lightHeadingCss: ThemeCss = theme => ({
  textTransform: `uppercase`,
  fontWeight: theme.fontWeights.body,
  color: theme.colors.grey[60],
  letterSpacing: theme.letterSpacings.tracked,
  fontSize: theme.fontSizes[0],
})

const listCss: ThemeCss = theme => ({
  margin: 0,
  marginTop: theme.space[5],
  listStyleType: `none`,
})

const itemCss: ThemeCss = theme => ({
  marginBottom: theme.space[7],

  "&:last-of-type": {
    marginBottom: 0,
  },
})

export type SslCertificatesProps = {
  hostingDetails: HostingDetails
  siteId: string
  allowToEdit?: boolean
  organizationId: string
}

export function SslCertificates({
  hostingDetails,
  siteId,
  allowToEdit,
  organizationId,
  ...rest
}: SslCertificatesProps) {
  const { flags } = useFlags()
  const { trackSegment, trackAction } = useTracker()
  const [showCertForm, setShowCertForm] = React.useState<boolean>(false)
  const [
    certificateToRemove,
    setCertificateToRemove,
  ] = React.useState<HostingCertificate | null>(null)
  const certificates = hostingDetails.certificates ?? []

  const { data } = useOrganizationPlanFieldsFragment(organizationId)
  const isNotFreePlan =
    Boolean(data?.billing?.plan?.baseFeatures?.tier) &&
    data?.billing?.plan?.baseFeatures?.tier !== MachinePricingTier.Free

  const domainsWithRequestedCert =
    hostingDetails.domains?.filter(item => {
      return item.certificateStatus === CertificateStatus.Requested
    }) ?? []

  const customCertAreAvailable = allowToEdit && isNotFreePlan

  return (
    <div css={rootCss} {...rest}>
      <div css={headerCss}>
        <Heading
          as="h4"
          css={theme => [headingCss(theme), lightHeadingCss(theme)]}
        >
          {text.headers.sslCertificates}
        </Heading>
        {customCertAreAvailable && (
          <Button
            onClick={() => setShowCertForm(true)}
            variant="GHOST"
            size="S"
          >
            {text.actions.useCustomCertificate}
          </Button>
        )}
      </div>
      <ul css={listCss}>
        {domainsWithRequestedCert.map(({ id }) => {
          return (
            <li css={itemCss} key={id}>
              <SslCertificateIsGenerating domain={id} />
            </li>
          )
        })}

        {certificates.map(cert => {
          const id = cert?.id

          if (!id) {
            return null
          }

          return (
            <li css={itemCss} key={id}>
              <SslCertificateDetails
                certificate={cert}
                onRemove={() => setCertificateToRemove(cert)}
                allowToEdit={allowToEdit}
              />
            </li>
          )
        })}
      </ul>

      {!!certificateToRemove && (
        <RemoveCertificateForm
          siteId={siteId}
          certificate={certificateToRemove}
          onDismiss={() => setCertificateToRemove(null)}
          onComplete={() => {
            setCertificateToRemove(null)

            trackAction({
              eventType: `TRACK_EVENT`,
              name: `SSL Certificate removed`,
              uiSource: `Site Settings`,
            })
            trackSegment({
              type: SegmentEventType.Track,
              event: "SSL Certificate removed",
              properties: {
                location: "Site Settings",
              },
            })
          }}
        />
      )}

      {showCertForm && (
        <StyledPanelBoilerplate
          isOpen={showCertForm}
          aria-label={text.headers.addDomain}
          onClose={() => setShowCertForm(false)}
          header={text.headers.useCustomCertificate}
        >
          <StyledPanelBodySection>
            <CustomCertificateForm
              siteId={siteId}
              onComplete={() => {
                setShowCertForm(false)

                trackAction({
                  eventType: `TRACK_EVENT`,
                  name: `SSL Certificate uploaded`,
                  uiSource: `Site Settings`,
                })
                trackSegment({
                  type: SegmentEventType.Track,
                  event: "SSL Certificate uploaded",
                  properties: {
                    location: "Site Settings",
                  },
                })
              }}
              onCancel={() => setShowCertForm(false)}
            />
          </StyledPanelBodySection>
        </StyledPanelBoilerplate>
      )}
    </div>
  )
}
